/**
 * PATH: packages/email-hub/src/feature/components/SenderIdentityCard.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-SENDERIDCARD-001
 * PURPOSE: Sender identity strip at the top of an open email — decides WHICH
 *          identity state applies (teammate / CRM / vendor) and renders its leaf.
 * WHY:     Operator (Abhishek, 2026-05-30): *"dcivil is a client.. it should
 *          have mapped it to Ford Civil and made a profile for Jamie .. tick
 *          and cross on top of the email"*. The reader showed the raw address
 *          with no CRM context and no way to file an unknown sender.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────────┐   ┌──────────────────┐
 *   │ email fields │ → │ predicates: which state │ → │ leaf renders it  │
 *   └──────────────┘   └────────────────────────┘   └──────────────────┘
 * MAIN EXPORTS: SenderIdentityCard (default) + the predicates/SENDER_ROLES
 *   re-exports its existing consumers import from this path.
 * NON-RESPONSIBILITIES: No gating logic (sender-identity/predicates), no
 *   mutations (sender-identity/useSenderIdentityActions), no state markup
 *   (sender-identity/*Identity.tsx).
 * RELATED:
 *   - quotech-backend organisation/views/email_views/sender_profile_views.py
 *   - useEmails.useEmail: parent passes ``refetch`` as onProfileCreated.
 *
 * NOTES FOR FUTURE AI: This file is a FRAME — hook calls, guards, JSX only.
 * Precedence between the states is the load-bearing part and each ordering step
 * is a shipped bug: teammate beats CRM (BUG-2026-07-09-INTERNAL-SENDER-CRM),
 * resolved-supplier beats category, and the "don't nag" guards beat everything
 * (operator 2026-05-31: "are you addicted to Add to CRM"). Reorder nothing
 * without re-reading predicates.ts. Split out of an 858-line monolith in
 * change #302; the leaves carry the original comments verbatim.
 */

import { cn } from '../../_internal/lib/utils'
import type { Email } from '../hooks/useEmails.types'
import { ResolvedIdentity, UnknownCrmIdentity } from './sender-identity/CrmIdentity'
import {
  TeammateIdentity,
  TeammateOverriddenIdentity,
} from './sender-identity/TeammateIdentity'
import { SavedVendorIdentity, UnfiledVendorIdentity } from './sender-identity/VendorIdentity'
import {
  inferredVendorKind,
  isInternalSender,
  isResolvedSupplier,
  isSenderResolved,
  isSubscriptionSupplier,
  isSupplierSender,
  isTeammateSender,
  shouldOfferAddToCrm,
} from './sender-identity/predicates'
import { useSenderIdentityActions } from './sender-identity/useSenderIdentityActions'

export {
  inferredVendorKind,
  isInternalSender,
  isResolvedSupplier,
  isRoleSender,
  isSenderResolved,
  isSubscriptionSupplier,
  isSupplierSender,
  isTeammateSender,
  looksLikeRelaySender,
  shouldOfferAddToCrm,
  shouldOfferManualCrmFiling,
  shouldShowSenderIdentity,
  type VendorKind,
} from './sender-identity/predicates'
export {
  SENDER_ROLES,
  type SenderRoleKey,
} from './sender-identity/useSenderIdentityActions'

interface SenderIdentityCardProps {
  email: Email
  /** Called after a profile is created so the parent can refetch the
   *  email detail (the server then returns the resolved contact/client
   *  and this strip re-renders into the green "known sender" state). */
  onProfileCreated?: () => void
  /** Inline-strip mode (the reader's Signal Strip WHO lane): a FLAT chip —
   *  no border, no fill, tighter type. A bordered pill inside a lane reads as
   *  a card nested in a card, which is exactly what the operator called
   *  "cluttered ugly" (2026-07-25). The standalone EmailReader leaves this
   *  off and keeps the full pill. */
  compact?: boolean
  className?: string
}

/**
 * Pick and render the one identity state this sender is in.
 *
 * Inputs: the (identity-enriched) email + the host's post-write refetch.
 * Output: one identity leaf, or null when the strip must stay silent.
 * Side effects: none directly — the leaves call the mutations hook.
 */
export default function SenderIdentityCard({
  email,
  onProfileCreated,
  compact = false,
  className,
}: SenderIdentityCardProps) {
  const actions = useSenderIdentityActions(email.id, onProfileCreated)
  const flat = compact
  const pad = compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-2 text-sm'

  // Teammate beats CRM: a stale Contact row carrying a colleague's address
  // must never render as a customer badge (BUG-2026-07-09-INTERNAL-SENDER-CRM).
  if (isTeammateSender(email) && !actions.teammateOverridden) {
    return (
      <TeammateIdentity
        email={email}
        actions={actions}
        flat={flat}
        pad={pad}
        className={className}
      />
    )
  }
  if (isTeammateSender(email) && actions.teammateOverridden) {
    return (
      <TeammateOverriddenIdentity
        actions={actions}
        flat={flat}
        pad={pad}
        className={className}
      />
    )
  }

  const resolved =
    (isSenderResolved(email) || Boolean(actions.created)) && !actions.detachedWrongPerson

  // Internal colleague mail (sender on our own mail domain) is never a CRM
  // lead or a supplier — render nothing unless they happen to be a resolved
  // contact (then the badge below still shows).
  if (!resolved && isInternalSender(email)) return null

  // Don't nag the operator to file marketing / newsletter / automated
  // senders into the CRM. Known senders still show the badge;
  // supplier/vendor mail gets the vendor states below.
  if (!resolved && !shouldOfferAddToCrm(email) && !isSupplierSender(email)) return null

  const senderLabel = email.from_name || email.from_address?.split('@')[0] || 'sender'

  // Supplier/vendor mail (AP-side): file the sender into the SUPPLIER master
  // (quotebuilder.Supplier), NOT the customer CRM. Operator (2026-06-02):
  // *"I should add that to not the CRM but Supplier… so we create a supplier
  // named that"*.
  if (isSupplierSender(email)) {
    const vendorLabel =
      email.company_label || email.from_name || email.from_address?.split('@')[0] || 'Vendor'
    const saved = actions.supplierSaved
    if (isResolvedSupplier(email) || saved) {
      return (
        <SavedVendorIdentity
          savedName={saved?.supplier_name || email.supplier_name || vendorLabel}
          kind={
            isSubscriptionSupplier(email) || saved?.kind === 'subscription'
              ? 'subscription'
              : 'supplier'
          }
          flat={flat}
          pad={pad}
          className={className}
        />
      )
    }
    return (
      <UnfiledVendorIdentity
        vendorLabel={vendorLabel}
        kind={inferredVendorKind(email)}
        actions={actions}
        flat={flat}
        pad={pad}
        className={className}
      />
    )
  }

  if (resolved) {
    return (
      <ResolvedIdentity
        contactName={actions.created?.contact_name ?? email.contact_name ?? null}
        clientName={actions.created?.client_name ?? email.client_name ?? null}
        actions={actions}
        flat={flat}
        pad={pad}
        className={className}
      />
    )
  }

  return (
    <UnknownCrmIdentity
      senderLabel={senderLabel}
      actions={actions}
      flat={flat}
      pad={pad}
      className={cn(className)}
    />
  )
}
